import React, { FunctionComponent, useEffect, useState } from 'react';
import { ImageDiagnosisValues } from './imageDiagnosisTypes';
import { ImageViewport } from './ImageViewport';
import { cn } from '@companion-professional/webutils';
import { ImageDiagnosisPoint } from './ImageDiagnosisPoint';
import { ImageDiagnosisCommonProps } from './index';
import { ImageSelector } from './ImageSelector';

interface ImageDiagnosisProps extends ImageDiagnosisCommonProps {
  // initialOptionValue (if provided) will be used as the starting value for each of the points on the image. If
  // initialOptionValue is not provided, an empty string will be used.
  initialOptionValue?: string | number;

  // onChange is a callback function that will be called whenever the user changes the value of a point.  The function
  // will be called with an object that contains the name of each point and the value that the user has selected for it.
  onChange?: (value: ImageDiagnosisValues) => void;

  // values is an object that contains the name of each point and the value that the user has selected for it.  Because
  // this is a controlled component (the state is managed by a parent component), passing in a new set of values is the
  // only way that the state of the component can be changed.  This can be done by using the onChange function in the
  // parent component.
  values?: ImageDiagnosisValues;
}

// ImageDiagnosis is a component that displays one or more diagnosis images (switchable by buttons, e.g. left side,
// right side) and displays "points" at various locations on each image (point like, left knee, back, etc.).  The user
// can then click these "points" and select a value (e.g. '-', '+', '++', etc) for each one of them.  The onChange
// function will be when the user selects a value for a point.
export const ImageDiagnosis: FunctionComponent<ImageDiagnosisProps> = ({
  label,
  name,
  diagnosisImages,
  options,
  initialOptionValue,
  onChange = () => {},
  values = {},
  disabled = false
}) => {
  const [currentlySelectedImage, setCurrentlySelectedImage] = useState<number>(0);

  useEffect(() => {
    // Set all the default values for the points when the component is first rendered.
    const newValues: ImageDiagnosisValues = {};
    diagnosisImages.forEach((di) => {
      if (di?.points) {
        di.points.forEach((point) => {
          const dv = initialOptionValue !== undefined ? initialOptionValue : '';
          newValues[point.name] = values?.[point.name] ?? dv;
        });
      }
    });
    onChange(newValues);
  }, []);

  return (
    <ImageSelector
      diagnosisImages={diagnosisImages}
      fieldName={name}
      fieldLabel={label}
      setCurrentlySelectedImage={setCurrentlySelectedImage}
      currentlySelectedImage={currentlySelectedImage}
    >
      {diagnosisImages.map((diagnosisImage, pageIndex) => (
        <ImageViewport
          key={`image-${name}-${diagnosisImage.name}-${pageIndex}`}
          className={cn({
            hidden: pageIndex !== currentlySelectedImage
          })}
          diagnosisImage={diagnosisImage}
        >
          {(diagnosisImage?.points || []).map((point, pointIndex) => (
            <ImageDiagnosisPoint
              key={`point-${name}-${diagnosisImage.name}-point-${pointIndex}`}
              point={point}
              options={options}
              selectedOptions={values || {}}
              onValueChange={(key: string, value: string | number) => {
                onChange({ ...values, [key]: value });
              }}
              pageIndex={pageIndex}
              disabled={disabled}
            />
          ))}
        </ImageViewport>
      ))}
    </ImageSelector>
  );
};
